import {localizeConversationDisplay} from "@/lib/i18n/conversation-copy"
import { useConversationTimeFormatters } from "@/hooks/use-conversation-time-formatters"
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { BotOff, Mail, ShieldOff } from 'lucide-react'
import { ChannelIcon } from '@/components/ui/channel-icon'
import type { ConversationListItem } from '@/hooks/agents/messaging/use-conversations'
import { pauseOriginLabel } from '@/lib/conversation/pause-origin-label'
import { resolveContactTitle } from '@/lib/agents/messaging/contact-title'
import { exclusionIdentity } from '@/lib/agents/messaging/contact-exclusions'
import { cn } from '@/lib/utils'

import { mediaMessageLabel } from '@/lib/conversation/message-preview'
import { contactInitials } from '@/lib/conversation/initials'
import { useTranslations } from "next-intl"



interface ConversationListItemProps {
  conversation: ConversationListItem
  isActive: boolean
  onClick: () => void
  timezone: string
}

function getPreviewText(conv: ConversationListItem): string {
  const prefix = conv.last_message_direction === 'outbound' ? 'You: ' : ''

  // Type first: a caption-less media message has no preview text, and reading "No messages yet"
  // on a conversation that just received a story is how a live thread looks abandoned.
  const mediaLabel = mediaMessageLabel(conv.last_message_type)
  if (mediaLabel) return `${prefix}${mediaLabel}`
  if (!conv.last_message_preview) return 'No messages yet'

  return `${prefix}${conv.last_message_preview}`
}

export function ConversationListItemComponent({
  conversation,
  isActive,
  onClick,
  timezone,
}: ConversationListItemProps) {
  const { formatListTimestamp } = useConversationTimeFormatters()


  const tCopy = useTranslations("ConversationCopy")
  const tUi = useTranslations("Messaging.AgentsMessagingInboxConversationListItem")

  const { title, subtitle } = localizeConversationDisplay(resolveContactTitle(conversation), tCopy)
  // Exactly what a typed exclusion entry would match on — never a captured
  // number, which the matcher does not read. Hidden when it IS the title
  // (Instagram rows are titled by their username).
  const key = exclusionIdentity(conversation)
  const identity =
    key && key.value.toLowerCase() !== title.toLowerCase()
      ? key.kind === 'handle' ? `@${key.value}` : key.value
      : null

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-3 px-3 py-3 text-left transition-colors border-b',
        isActive
          ? 'bg-blue-50 dark:bg-blue-950/30 border-l-2 border-l-blue-500'
          : 'hover:bg-muted/50 border-l-2 border-l-transparent',
        conversation.unread_count > 0 && !isActive && 'bg-blue-50/40 dark:bg-blue-950/10',
        // Identifiable without opening the thread; the active row keeps its own blue rail.
        conversation.auto_pause_exempt && !isActive && 'border-l-violet-400 dark:border-l-violet-500',
      )}
    >
      <div className="relative flex-shrink-0">
        <Avatar className="h-10 w-10">
          <AvatarImage src={conversation.contact_avatar_url ?? undefined} />
          <AvatarFallback className="text-xs">{contactInitials(title)}</AvatarFallback>
        </Avatar>
        <div className="absolute -bottom-0.5 -right-0.5">
          <ChannelIcon channelType={conversation.channel_type} size={12} />
        </div>
        {conversation.unread_count > 0 && (
          <div className="absolute -top-0.5 -left-0.5 h-2.5 w-2.5 rounded-full bg-blue-500 ring-2 ring-background" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={cn(
            'text-sm truncate',
            conversation.unread_count > 0 ? 'font-semibold' : 'font-medium',
          )}>
            {title}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            {conversation.auto_pause_exempt && (
              <span
                title={tUi("autoPauseOffHumanRepliesDoNotPause")}
                className="flex items-center gap-0.5 text-violet-600 dark:text-violet-400"
              >
                <ShieldOff className="h-3 w-3" />
                <span className="text-[10px] leading-none"><span key={tUi("noAutoPause")}>{tUi("noAutoPause")}</span></span>
              </span>
            )}
            {conversation.last_message_offline && (
              <span
                title={tUi("theLastReplyWasTheAutomatedOfflineMessage")}
                className="flex items-center gap-0.5 text-slate-500 dark:text-slate-400"
              >
                <Mail className="h-3 w-3" />
                <span className="text-[10px] leading-none"><span key={tUi("offlineReply")}>{tUi("offlineReply")}</span></span>
              </span>
            )}
            {conversation.ai_paused && (
              <span
                title={tUi("aIPausedValue0", { value0: String(pauseOriginLabel(conversation.ai_pause_by)) })}
                className="flex items-center gap-0.5 text-amber-500"
              >
                <BotOff className="h-3 w-3" />
                <span className="text-[10px] leading-none">{pauseOriginLabel(conversation.ai_pause_by)}</span>
              </span>
            )}
            <span className={cn(
              'text-xs',
              conversation.unread_count > 0 ? 'text-blue-600 font-medium' : 'text-muted-foreground',
            )}>
              {formatListTimestamp(conversation.last_message_at, timezone)}
            </span>
          </div>
        </div>
        {(identity || subtitle) && (
          <p className="text-[11px] text-muted-foreground truncate" title={identity ?? subtitle!}>
            {identity && <span className="font-mono">{identity}</span>}
            {identity && subtitle && <span aria-hidden> · </span>}
            {subtitle}
          </p>
        )}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p className={cn(
            'text-xs truncate',
            conversation.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
          )}>
            {getPreviewText(conversation)}
          </p>
          {conversation.unread_count > 0 && (
            <Badge className="h-5 min-w-5 px-1.5 text-[10px] flex-shrink-0">
              {conversation.unread_count}
            </Badge>
          )}
        </div>
        {conversation.shopify_demo_domain && (
          <p
            className="text-[10px] text-amber-700 dark:text-amber-400 truncate mt-0.5 flex items-center gap-1"
            title={tUi("demoStorefrontValue0", { value0: String(conversation.shopify_demo_domain) })}
          >
            <span aria-hidden>🏪</span>
            <span className="font-mono truncate">{conversation.shopify_demo_domain}</span>
          </p>
        )}
      </div>
    </button>
  )
}
